/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {AccordionModule} from 'primeng/accordion';
import {ConfirmationService, MessageService} from 'primeng/api';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {AvatarModule} from 'primeng/avatar';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {CheckboxModule} from 'primeng/checkbox';
import {ChipModule} from 'primeng/chip';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmPopupModule} from 'primeng/confirmpopup';
import {DatePickerModule} from 'primeng/datepicker';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {DialogService, DynamicDialogModule} from 'primeng/dynamicdialog';
import {EditorModule} from 'primeng/editor';
import {FieldsetModule} from 'primeng/fieldset';
import {FloatLabelModule} from 'primeng/floatlabel';
import {FileUploadModule} from 'primeng/fileupload';
import {ImageModule} from 'primeng/image';
import {InputMaskModule} from 'primeng/inputmask';
import {InputNumberModule} from 'primeng/inputnumber';
import {InputTextModule} from 'primeng/inputtext';
import {KeyFilterModule} from 'primeng/keyfilter';
import {ListboxModule} from 'primeng/listbox';
import {MessageModule} from 'primeng/message';
import {MenubarModule} from 'primeng/menubar';
import {MenuModule} from 'primeng/menu';
import {MultiSelectModule} from 'primeng/multiselect';
import {PanelModule} from 'primeng/panel';
import {PickListModule} from 'primeng/picklist';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {RippleModule} from 'primeng/ripple';
import {SelectModule} from 'primeng/select';
import {SplitButtonModule} from 'primeng/splitbutton';
import {TabsModule} from 'primeng/tabs';
import {TableModule} from 'primeng/table';
import {TextareaModule} from 'primeng/textarea';
import {TieredMenuModule} from 'primeng/tieredmenu';
import {ToastModule} from 'primeng/toast';
import {ToggleSwitchModule} from 'primeng/toggleswitch';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {ToolbarModule} from 'primeng/toolbar';
import {TreeModule} from 'primeng/tree';
import {TreeTableModule} from 'primeng/treetable';
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {GuestInfoPanelComponent} from './components/guest-info-panel/guest-info-panel.component';
import {AuthenticationModule} from './core/authentication/authentication.module';
import {CoreModule} from './core/core.module';
import {AuthGuardService} from './core/guards/auth-guard.service';
import {HttpInterceptors} from './core/interceptors/http-interceptors';
import {LayoutModule} from './layout/layout.module';
import {MenuService} from './layout/menu.service';
import {AppInfoService} from './service/app-info.service';
import {HelpInfoComponent} from './components/help-info/help-info.component';
import {BuildCheckToolComponent} from './components/build-check-tool/build-check-tool.component';
import {Crm2Component} from './components/crm2/crm2.component';
import {BackToEsciComponent} from './components/back-to-esci/back-to-esci.component';
import {NetlistCompareComponent} from './components/netlist-compare/netlist-compare.component';
import {ExtractionCompareComponent} from './components/extraction-compare/extraction-compare.component';
import {IdenticalConnectorComponent} from './components/identical-connector/identical-connector.component';
import {ProgressBarModule} from 'primeng/progressbar';
import {WelcomeComponent} from './components/welcome/welcome-component';
import {AgGridModule} from 'ag-grid-angular';
import {KtsComponent} from './components/kts/kts.component';
import {ButtonCellRendererComponent} from './components/cell-renderer/button-cell-renderer.component';
import {GanttChartComponent} from './components/gantt-chart/gantt-chart.component';
import {KtsEditorComponent} from './components/kts-editor/kts-editor.component';
import {ActionsCellRendererComponent} from './components/cell-renderer/actions-cell-renderer.component';
import {ProgramRunsComponent} from './components/program-runs/program-runs.component';
import {TypeaheadCellEditorComponent} from './components/cell-renderer/typeahead-cell-editor.component';
import {VehicleBuildCompareComponent} from './components/vehicle-build-compare/vehicle-build-compare.component';
import {FileUploadCardComponent} from './components/file-upload-card/file-upload-card.component';
import {TagModule} from 'primeng/tag';
import {AutoSearchComponent} from './components/auto-search/auto-search.component';
import {GpsRpoManagementComponent} from './components/gps-rpo-management/gps-rpo-management.component';
import {UserSearchCellEditorComponent} from './components/cell-renderer/user-search-cell-editor.component';
import {GpsTimingComponent} from './components/gps-timing/gps-timing.component';
import {
    CustomMultiSelectFilterComponent
} from './components/filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from './components/filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {RestrictedProgramsComponent} from './components/restricted-programs/restricted-programs.component';
import {ModalCellEditorComponent} from './components/cell-renderer/modal-cell-editor.component';
import {KtsAdminComponent} from './components/kts-admin/kts-admin.component';
import {SkeletonModule} from 'primeng/skeleton';
import {ButtonCellRendererGpsComponent} from './components/cell-renderer/button-cell-renderer-gps.component';
import {IcdProgramTimingComponent} from './components/icd-program-timing/icd-program-timing.component';
import {EsciComponentGridComponent} from './components/esci-component-grid/esci-component-grid.component';
import {
    MultiSelectDropdownCellEditorComponent
} from './components/cell-renderer/multi-select-dropdown-cell-editor.component';
import {ImageCellRenderComponent} from './components/cell-renderer/image-cell-render.component';
import {MultiImageCellRenderComponent} from './components/cell-renderer/multi-image-cell-render.component';
import {AttachmentCellRendererComponent} from './components/cell-renderer/attachment-cell-renderer.component';
import {TooltipImageComponent} from './components/cell-renderer/tooltip-image.component';
import {ConfigurationComponent} from './components/configuration/configuration.component';
import {ChangeRolesComponent} from './components/change-roles/change-roles.component';
import {ListManagerComponent} from './components/list-manager/list-manager.component';
import {WorkloadTimingComponent} from './components/workload-timing/workload-timing.component';
import {SvgBarChartComponent} from './components/svg-bar-chart/svg-bar-chart.component';
import {ObjectDropdownCellEditorComponent} from './components/cell-renderer/object-dropdown-cell-editor.component';
import {UrlCellRendererComponent} from './components/cell-renderer/url-cell-renderer.component';
import {ImageWithTooltipComponent} from './components/cell-renderer/image-with-tooltip.component';
import {HoleCriteriaEditorComponent} from './components/cell-renderer/hole-criteria-editor.component';
import {ViewDetailDialogComponent} from './components/cell-renderer/view-detail-dialog.component';
import {
    DiagTestProbeRelationshipDialogComponent
} from './components/cell-renderer/diag-test-probe-relationship-dialog.component';
import {
    TerminalReleaseToolRelationshipDialogComponent
} from './components/cell-renderer/terminal-release-tool-relationship-dialog.component';
import {CustomNoRowsOverlayComponent} from './components/custom-no-rows-overlay-component';
import {AdvancedFilterModalComponent} from './components/filter-components/advanced-filter-modal.component';
import {
    DevelopmentStatusReasonDialogComponent
} from './components/development-status-reason-dialog/development-status-reason-dialog.component';
import {CollapsibleGroupHeaderComponent} from './components/CollapsibleGroupHeaderComponent';
import {ViewDetailContentComponent} from './components/cell-renderer/view-detail-content.component';
import {
    DropdownWithDescriptionCellRendererComponent
} from './components/cell-renderer/dropdown-with-description-cell-renderer.component';
import {
    UrlRequestActivityReportComponent
} from './components/url-request-activity-report/url-request-activity-report.component';
import {ClearCachesComponent} from './components/clear-caches/clear-caches.component';
import {ImportFileDataZipComponent} from './components/import-file-data-zip/import-file-data-zip.component';
import {ReleaseScheduleComponent} from './components/release-schedule/release-schedule.component';
import {IcdSubscriptionsComponent} from './components/icd-subscriptions/icd-subscriptions.component';
import {IcdReportingComponent} from './components/icd-reporting/icd-reporting.component';
import {ServerPagingPanelComponent} from './components/server-paging/server-paging-panel.component';
import {ActiveSearchCriteriaComponent} from './components/active-search-criteria/active-search-criteria.component';
import {
    NumericListFloatingFilterComponent
} from './components/filter-components/numeric-list-floating-filter/numeric-list-floating-filter.component';
import {CapitalComponentUsageComponent} from './components/capital-component-usage/capital-component-usage.component';
import {IoMatrixWorkflowComponent} from './components/io-matrix-workflow/io-matrix-workflow.component';
import {IoMatrixComponent} from './components/io-matrix-workflow/io-matrix/io-matrix.component';
import {IoMatrixIcdsComponent} from './components/io-matrix-workflow/io-matrix-icds/io-matrix-icds.component';
import {CreateIcdsComponent} from './components/io-matrix-workflow/create-icds/create-icds.component';
import {AggregatorSignalTableComponent} from './components/aggregator-signal-table/aggregator-signal-table.component';
import {ConfirmEnterAcceptDirective} from './utils/confirm-enter-accept.directive';
import {AggregatorIoMatrixComponent} from './components/aggregator-io-matrix/aggregator-io-matrix.component';
import {
    GmPrimaryButtonDirective,
    GmSecondaryButtonDirective,
    GmTertiaryButtonDirective
} from './shared/ui/gm-compat/gm-button-intent.directive';
import {GmLoadingBlockComponent} from './shared/ui/gm-compat/gm-loading-block.component';
import {ChildGridComponent} from './components/cell-renderer/child-grid.component';
import {IcdTemplatePinCellEditorComponent} from './components/cell-renderer/icd-template-pin-cell-editor.component';
import {TooltipModule} from 'primeng/tooltip';

@NgModule({
    declarations: [
        AppComponent,
        HelpInfoComponent,
        GuestInfoPanelComponent,
        WelcomeComponent,
        BuildCheckToolComponent,
        Crm2Component,
        NetlistCompareComponent,
        IdenticalConnectorComponent,
        ExtractionCompareComponent,
        BackToEsciComponent,
        KtsComponent,
        ButtonCellRendererComponent,
        ButtonCellRendererGpsComponent,
        GanttChartComponent,
        ActionsCellRendererComponent,
        UrlCellRendererComponent,
        KtsEditorComponent,
        ProgramRunsComponent,
        TypeaheadCellEditorComponent,
        MultiSelectDropdownCellEditorComponent,
        ObjectDropdownCellEditorComponent,
        DropdownWithDescriptionCellRendererComponent,
        AttachmentCellRendererComponent,
        ImageCellRenderComponent,
        HoleCriteriaEditorComponent,
        MultiImageCellRenderComponent,
        VehicleBuildCompareComponent,
        FileUploadCardComponent,
        AutoSearchComponent,
        GpsRpoManagementComponent,
        UserSearchCellEditorComponent,
        GpsTimingComponent,
        CustomMultiSelectFilterComponent,
        CustomMultiSelectFloatingFilterComponent,
        NumericListFloatingFilterComponent,
        RestrictedProgramsComponent,
        ModalCellEditorComponent,
        TooltipImageComponent,
        KtsAdminComponent,
        IcdProgramTimingComponent,
        EsciComponentGridComponent,
        CustomNoRowsOverlayComponent,
        ConfigurationComponent,
        ChangeRolesComponent,
        ListManagerComponent,
        WorkloadTimingComponent,
        SvgBarChartComponent,
        ImageWithTooltipComponent,
        ViewDetailContentComponent,
        ViewDetailDialogComponent,
        DiagTestProbeRelationshipDialogComponent,
        TerminalReleaseToolRelationshipDialogComponent,
        AdvancedFilterModalComponent,
        DevelopmentStatusReasonDialogComponent,
        CollapsibleGroupHeaderComponent,
        UrlRequestActivityReportComponent,
        ClearCachesComponent,
        ImportFileDataZipComponent,
        ReleaseScheduleComponent,
        IcdSubscriptionsComponent,
        IcdReportingComponent,
        ServerPagingPanelComponent,
        ActiveSearchCriteriaComponent,
        IoMatrixWorkflowComponent,
        IoMatrixComponent,
        IoMatrixIcdsComponent,
        CreateIcdsComponent,
        IcdTemplatePinCellEditorComponent,
        CapitalComponentUsageComponent,
        AggregatorSignalTableComponent,
    ],
    imports: [
        AuthenticationModule,
        HttpClientModule,
        BrowserModule,
        DragDropModule,
        AppRoutingModule,
        CoreModule,
        LayoutModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        SelectModule,
        TableModule,
        TabsModule,
        ButtonModule,
        RippleModule,
        InputTextModule,
        AccordionModule,
        ConfirmPopupModule,
        DynamicDialogModule,
        PickListModule,
        MessageModule,
        MenuModule,
        ConfirmDialogModule,
        DialogModule,
        ToastModule,
        CheckboxModule,
        TextareaModule,
        ToolbarModule,
        MultiSelectModule,
        FileUploadModule,
        CardModule,
        AutoCompleteModule,
        AvatarModule,
        KeyFilterModule,
        InputMaskModule,
        GmPrimaryButtonDirective,
        GmSecondaryButtonDirective,
        GmTertiaryButtonDirective,
        ConfirmEnterAcceptDirective,
        InputNumberModule,
        GmLoadingBlockComponent,
        MenubarModule,
        FieldsetModule,
        FloatLabelModule,
        PanelModule,
        SplitButtonModule,
        DividerModule,
        ToggleSwitchModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        TieredMenuModule,
        TreeModule,
        TreeTableModule,
        ToggleButtonModule,
        ChipModule,
        DatePickerModule,
        ListboxModule,
        ImageModule,
        EditorModule,
        AgGridModule,
        ChildGridComponent,
        AggregatorIoMatrixComponent,
        TagModule,
        SkeletonModule,
        TooltipModule
    ],
    providers: [
        AppInfoService,
        AuthGuardService,
        MessageService,
        MenuService,
        ConfirmationService,
        DialogService, /* used to enable open modal dialogs */
        {provide: 'environment', useValue: environment},
        {provide: HTTP_INTERCEPTORS, useClass: HttpInterceptors, multi: true},
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
